/**
 * Box Helper - FluentUI Design Kit
 * Token-mapping helpers extracted from the Box primitive.
 */

import type { CSSProperties } from 'react';
import { useFluentTokens } from '../../Provider';

// Design Kit spacing tokens (matches tokens.ts)
export type SpacingToken = 'spacingVerticalXXS' | 'spacingVerticalXS' | 'spacingVerticalS' | 'spacingVerticalM' | 'spacingVerticalL' | 'spacingVerticalXL' | 'spacingVerticalXXL';
export type SpacingSize = 'xxs' | 'xs' | 's' | 'm' | 'l' | 'xl' | 'xxl';

// Border radius tokens
export type RadiusToken = 'borderRadiusSmall' | 'borderRadiusMedium' | 'borderRadiusLarge' | 'borderRadiusXLarge' | 'borderRadiusXXLarge' | 'borderRadiusXXXLarge' | 'borderRadiusXXXXLarge';
export type RadiusSize = 'small' | 'medium' | 'large' | 'xlarge' | 'xxlarge' | 'xxxlarge' | 'xxxxlarge';

type BgColor = 'surface1' | 'surface2' | 'surface3' | 'brand1' | 'brand2';
type TextColor = 'text1' | 'text2' | 'brand1' | 'brand2';

export type BoxStyleProps = {
    p?: SpacingSize; px?: SpacingSize; py?: SpacingSize;
    pt?: SpacingSize; pr?: SpacingSize; pb?: SpacingSize; pl?: SpacingSize;
    m?: SpacingSize; mx?: SpacingSize; my?: SpacingSize;
    mt?: SpacingSize; mr?: SpacingSize; mb?: SpacingSize; ml?: SpacingSize;
    bg?: BgColor;
    color?: TextColor;
    borderRadius?: RadiusSize;
    display?: CSSProperties['display'];
    flexDirection?: CSSProperties['flexDirection'];
    alignItems?: CSSProperties['alignItems'];
    justifyContent?: CSSProperties['justifyContent'];
    gap?: SpacingSize;
    width?: CSSProperties['width'];
    height?: CSSProperties['height'];
    maxWidth?: CSSProperties['maxWidth'];
    maxHeight?: CSSProperties['maxHeight'];
    style?: CSSProperties;
};

export const useBoxHelpers = () => {
    const { tokens: designKitTokens, modeTokens } = useFluentTokens();

    const getSpacing = (size: SpacingSize): string => {
        const tokenMap: Record<SpacingSize, SpacingToken> = {
            xxs: 'spacingVerticalXXS',
            xs: 'spacingVerticalXS',
            s: 'spacingVerticalS',
            m: 'spacingVerticalM',
            l: 'spacingVerticalL',
            xl: 'spacingVerticalXL',
            xxl: 'spacingVerticalXXL',
        };
        return designKitTokens[tokenMap[size]];
    };

    const getBackgroundColor = (bgColor: BgColor): string => {
        const colorMap = {
            surface1: modeTokens.colorNeutralBackground1,
            surface2: modeTokens.colorNeutralBackground2,
            surface3: modeTokens.colorNeutralBackground3,
            brand1: modeTokens.colorBrand80,
            brand2: modeTokens.colorBrand90,
        };
        return colorMap[bgColor];
    };

    const getTextColor = (textColor: TextColor): string => {
        const colorMap = {
            text1: modeTokens.colorNeutralForeground1,
            text2: modeTokens.colorNeutralForeground2,
            brand1: modeTokens.colorBrand80,
            brand2: modeTokens.colorBrand90,
        };
        return colorMap[textColor];
    };

    const getBorderRadius = (radius: RadiusSize): string => {
        const radiusMap: Record<RadiusSize, RadiusToken> = {
            small: 'borderRadiusSmall',
            medium: 'borderRadiusMedium',
            large: 'borderRadiusLarge',
            xlarge: 'borderRadiusXLarge',
            xxlarge: 'borderRadiusXXLarge',
            xxxlarge: 'borderRadiusXXXLarge',
            xxxxlarge: 'borderRadiusXXXXLarge',
        };
        return designKitTokens[radiusMap[radius]];
    };

    const buildSpacingStyles = (props: BoxStyleProps): CSSProperties => ({
        ...(props.p && { padding: getSpacing(props.p) }),
        ...(props.px && { paddingLeft: getSpacing(props.px), paddingRight: getSpacing(props.px) }),
        ...(props.py && { paddingTop: getSpacing(props.py), paddingBottom: getSpacing(props.py) }),
        ...(props.pt && { paddingTop: getSpacing(props.pt) }),
        ...(props.pr && { paddingRight: getSpacing(props.pr) }),
        ...(props.pb && { paddingBottom: getSpacing(props.pb) }),
        ...(props.pl && { paddingLeft: getSpacing(props.pl) }),
        ...(props.m && { margin: getSpacing(props.m) }),
        ...(props.mx && { marginLeft: getSpacing(props.mx), marginRight: getSpacing(props.mx) }),
        ...(props.my && { marginTop: getSpacing(props.my), marginBottom: getSpacing(props.my) }),
        ...(props.mt && { marginTop: getSpacing(props.mt) }),
        ...(props.mr && { marginRight: getSpacing(props.mr) }),
        ...(props.mb && { marginBottom: getSpacing(props.mb) }),
        ...(props.ml && { marginLeft: getSpacing(props.ml) }),
    });

    const buildBoxStyles = (props: BoxStyleProps): CSSProperties => ({
        ...buildSpacingStyles(props),
        ...(props.bg && { backgroundColor: getBackgroundColor(props.bg) }),
        ...(props.color && { color: getTextColor(props.color) }),
        ...(props.borderRadius && { borderRadius: getBorderRadius(props.borderRadius) }),
        ...(props.display && { display: props.display }),
        ...(props.flexDirection && { flexDirection: props.flexDirection }),
        ...(props.alignItems && { alignItems: props.alignItems }),
        ...(props.justifyContent && { justifyContent: props.justifyContent }),
        ...(props.gap && { gap: getSpacing(props.gap) }),
        ...(props.width && { width: props.width }),
        ...(props.height && { height: props.height }),
        ...(props.maxWidth && { maxWidth: props.maxWidth }),
        ...(props.maxHeight && { maxHeight: props.maxHeight }),
        ...props.style,
    });

    return { getSpacing, getBackgroundColor, getTextColor, getBorderRadius, buildBoxStyles };
};
